// DispositionDropdown — the Ringy-style DISPOSITION button on the Lead Detail
// left column. Presents the agency's preset disposition list; selecting one
// POSTs to /api/leads/:id/disposition (tenant-scoped server-side), which sets
// lead.disposition, applies the optional status transition, and enrolls the
// lead in the optional drip campaign.
//
// The preset list mirrors the agency's Ringy tags 1:1. Each preset can carry:
//   - status: a Lead.status transition to apply alongside the disposition
//   - drip:   a drip-campaign slug to enroll the lead into

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api.js';
import useQuickSale from '../QuickSaleModal.jsx';

// Preset dispositions — order matches the agency's Ringy configuration.
export const DISPOSITION_PRESETS = [
  { label: 'No Show',                 status: 'NO_SHOW' },
  { label: 'Sold',                    status: 'SOLD' },
  { label: 'Scheduled Appointment',   status: 'BOOKED_APPOINTMENT' },
  { label: 'Not Interested',          status: 'NOT_INTERESTED' },
  { label: 'Quoted',                  status: 'QUOTED' },
  { label: 'Transferred',             status: 'CONTACTED' },
  { label: 'No Sale',                 status: 'NO_SALE' },
  { label: 'IUL LEADS',               drip: 'iul-leads' },
  { label: '2nd Chance IUL',          drip: '2nd-chance-iul' },
  { label: 'Aged Mortgage Leads',     drip: 'aged-mortgage' },
  { label: 'CRM New MP Leads',        drip: 'crm-new-mp' },
  { label: 'CRM Internet Leads',      drip: 'crm-internet' },
  { label: 'CRM Veterans Leads',      drip: 'crm-veterans' },
  { label: 'Civix Veteran Leads',     drip: 'civix-veteran' },
  { label: 'Ethos Leads Drip',        drip: 'ethos-leads' },
  { label: 'Last Chance Ethos Drip',  drip: 'last-chance-ethos' },
  { label: 'Licensed Agents Text 1',  drip: 'licensed-agents-text-1' },
];

export default function DispositionDropdown({ leadId, current, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null); // label being saved
  const ref = useRef(null);
  const { askSale, saleModal } = useQuickSale();
  const { data: dispData } = useQuery({ queryKey: ['dispositions'], queryFn: () => apiGet('/dispositions').catch(() => null), staleTime: 60_000 });
  const presetLabels = new Set(DISPOSITION_PRESETS.map((p) => p.label.toLowerCase()));
  // The agency's own tags (Marketing Hub → Dispositions) that are not already in the built-in list.
  const custom = (dispData?.dispositions || [])
    .filter((d) => !presetLabels.has(String(d.label).toLowerCase()) && !d.systemCode)
    .map((d) => ({ label: d.label, dispositionId: d._id }));
  const options = [...DISPOSITION_PRESETS, ...custom];

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  async function apply(preset) {
    let sale = null;
    if (preset.status === 'SOLD') {
      sale = await askSale();
      if (sale == null) return;
    }
    setSaving(preset.label);
    try {
      if (preset.dispositionId) {
        // Custom tag: applying it also starts its Drip Action, same as the leads table.
        const r = await apiPost('/dispositions/apply', { leadId, dispositionId: preset.dispositionId });
        if (r?.ok === false) throw new Error(r.error || 'Failed');
        toast.success(`Disposition set: ${preset.label}`);
        if (r?.warning) toast(r.warning, { icon: '⚠️', duration: 8000 });
        setOpen(false);
        onChange?.(preset);
        return;
      }
      const body = { disposition: preset.label };
      if (preset.status) body.status = preset.status;
      if (sale) { body.annualPremium = sale.annualPremium; body.firstDraftDate = sale.firstDraftDate; }
      if (preset.drip) body.drip = preset.drip;
      const res = await apiPost(`/leads/${leadId}/disposition`, body);
      if (res?.ok === false) throw new Error(res.error || 'Failed');
      toast.success(`Disposition set: ${preset.label}`);
      setOpen(false);
      onChange?.(preset);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to set disposition');
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="relative" ref={ref}>
      {saleModal}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg
                   bg-civix-panel border border-civix-border text-xs font-semibold
                   text-white/80 hover:border-civix-purple/60 transition"
      >
        <span className="truncate">{current || 'DISPOSITION'}</span>
        <ChevronDown className="w-4 h-4 shrink-0" />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg
                     bg-civix-panel border border-civix-border shadow-xl py-1"
        >
          {options.map((preset) => (
            <li key={preset.label} role="option" aria-selected={current === preset.label}>
              <button
                type="button"
                disabled={!!saving}
                onClick={() => apply(preset)}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left
                           text-xs text-white/75 hover:bg-civix-purple/15 disabled:opacity-50"
              >
                <span className="truncate">{preset.label}</span>
                {saving === preset.label
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  : current === preset.label
                    ? <Check className="w-3.5 h-3.5 text-civix-cyan shrink-0" />
                    : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
